import {
  type CareThread,
  type ClassMood,
  MOODS,
  type ViewContext,
  addDays,
  shortDate,
  timeOf,
  todayIso,
  useCareActions,
  useCareThreads,
  useMoodOverview,
} from '@shkola/api';
import { ArrowDownIcon } from '@phosphor-icons/react/ArrowDown';
import { ArrowUpIcon } from '@phosphor-icons/react/ArrowUp';
import { LockSimpleIcon } from '@phosphor-icons/react/LockSimple';
import { Link, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, EmptyState, Spinner } from '../components/ui';
import {
  Block,
  DEEP,
  EventArt,
  GRAD,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  PillIcon,
  Tabs,
  pillClass,
  ringButton,
} from '../design/forms';
import { ArrowUpRightIcon } from '../design/icons';
import { MoodArt } from '../student/home/LineArt';
import { IconDot, Private, ThreadView, threadLook, threadTitle, useWhoLabel } from './shared';

/*
 * «Турбота» of those who answer, in «Форми 2» (28.10): the psychologist (every conversation, the mood of
 * the classes, the meetings) and the director (the reports of bullying only). The title on the paper with
 * the promise of anonymity under it; the conversations — a list with filter chips on one block, the open
 * one on another; the mood of the classes as rows with a lavender bar and the faces of the moods.
 */

type Filter = 'all' | 'unread' | 'urgent' | 'bullying' | 'closed';

function matches(thread: CareThread, filter: Filter) {
  switch (filter) {
    case 'all':
      return thread.status === 'open';
    case 'unread':
      return thread.unreadForStaff;
    case 'urgent':
      return thread.urgent && thread.status === 'open';
    case 'bullying':
      return thread.kind === 'bullying' && thread.status === 'open';
    case 'closed':
      return thread.status === 'closed';
  }
}

const ORANGE_DOT = '#FF6B3D';

function ThreadRow({
  thread,
  active,
  onClick,
}: {
  thread: CareThread;
  active: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const whoLabel = useWhoLabel();
  const day = thread.lastMessageAt.slice(0, 10);
  const look = threadLook(thread);
  return (
    <button
      type="button"
      aria-current={active || undefined}
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-[18px] px-3 py-2.5 text-left transition-colors ${active ? '' : 'hover:bg-[#FAF9F6]'}`}
      style={{ backgroundColor: active ? PAPER : undefined }}
    >
      <IconDot icon={look.icon} tone={look.tone} />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-2">
          <span
            className={`truncate text-[15px] leading-5 ${thread.unreadForStaff ? 'font-medium' : ''}`}
            style={{ color: INK }}
          >
            {threadTitle(t, thread)}
          </span>
          {thread.urgent && <Badge tone="danger">{t('care.threads.urgent')}</Badge>}
        </span>
        <span
          className="mt-0.5 flex min-w-0 items-center gap-1 text-[13px] leading-[18px]"
          style={{ color: INK_SOFT }}
        >
          {!thread.authorName && (
            <LockSimpleIcon size={12} weight="bold" aria-hidden className="shrink-0" />
          )}
          <span className="truncate">{whoLabel(thread)}</span>
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end gap-1.5">
        <span className="text-[12px]" style={{ color: INK_FAINT }}>
          {day === todayIso() ? timeOf(thread.lastMessageAt) : shortDate(day)}
        </span>
        {thread.unreadForStaff && (
          <span className="size-2.5 rounded-full" style={{ backgroundColor: ORANGE_DOT }} />
        )}
      </span>
    </button>
  );
}

function Inbox({ ctx, directorOnly }: { ctx: ViewContext; directorOnly: boolean }) {
  const { t } = useTranslation();
  const search = useSearch({ strict: false }) as { thread?: string };
  const threads = useCareThreads(ctx.schoolId);
  const actions = useCareActions();
  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState<string | undefined>(search.thread);
  const whoLabel = useWhoLabel();
  const view = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (search.thread) setSelected(search.thread);
  }, [search.thread]);

  const open = (id: string) => {
    setSelected(id);
    // on a phone the conversation stands under the list: bring it up
    if (!window.matchMedia('(min-width: 1024px)').matches)
      requestAnimationFrame(() =>
        view.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      );
  };

  const all = threads.data ?? [];
  const shown = all
    .filter((th) => matches(th, filter))
    // urgent and unanswered first, then by the last message
    .sort(
      (a, b) =>
        Number(b.urgent && b.lastFromStudent) - Number(a.urgent && a.lastFromStudent) ||
        b.lastMessageAt.localeCompare(a.lastMessageAt),
    );
  // only what the psychologist clicked: opening marks a thread read, so nothing opens by itself
  const current = all.find((th) => th.id === selected);
  const count = (f: Filter) => all.filter((th) => matches(th, f)).length;
  const filters = (['all', 'unread', 'urgent', 'bullying', 'closed'] as const).filter(
    (f) => !directorOnly || f !== 'bullying',
  );

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[380px_minmax(0,1fr)]">
      <Block className="!p-3 md:!p-4">
        <div className="mb-3 flex flex-wrap gap-1.5 px-1 pt-1">
          {filters.map((f) => {
            const on = filter === f;
            const n = count(f);
            return (
              <button
                key={f}
                type="button"
                aria-pressed={on}
                onClick={() => setFilter(f)}
                className={`inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[14px] transition-colors ${on ? 'font-medium' : 'hover:bg-[#EFECE5]'}`}
                style={{ backgroundColor: on ? '#E6E1D6' : PAPER, color: INK }}
              >
                {t(`care.staff.filters.${f}`)}
                {f !== 'closed' && n > 0 && (
                  <span
                    style={{
                      color: f === 'urgent' || f === 'unread' ? GRAD.coral.ink : INK_FAINT,
                    }}
                  >
                    {n}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {threads.isPending ? (
          <Spinner />
        ) : shown.length === 0 ? (
          <EmptyState>{t('care.staff.empty')}</EmptyState>
        ) : (
          <div className="flex flex-col gap-0.5">
            {shown.map((th) => (
              <ThreadRow
                key={th.id}
                thread={th}
                active={th.id === current?.id}
                onClick={() => open(th.id)}
              />
            ))}
          </div>
        )}
      </Block>
      <div
        ref={view}
        className={`min-w-0 scroll-mt-4 lg:sticky lg:top-20 ${current ? '' : 'hidden lg:block'}`}
      >
        <Block>
          {current ? (
            <>
              <header className="mb-4 flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  <h2
                    className="font-display text-[28px] leading-[34px] font-light tracking-[-0.03em]"
                    style={{ color: INK }}
                  >
                    {threadTitle(t, current)}
                  </h2>
                  <p
                    className="mt-0.5 flex items-center gap-1.5 text-[14px]"
                    style={{ color: INK_SOFT }}
                  >
                    {!current.authorName && (
                      <LockSimpleIcon size={13} weight="bold" aria-hidden className="shrink-0" />
                    )}
                    {current.authorName
                      ? whoLabel(current)
                      : `${t('care.staff.anonymous')}, ${whoLabel(current)}`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    actions.setStatus.mutate({
                      threadId: current.id,
                      status: current.status === 'open' ? 'closed' : 'open',
                    })
                  }
                  className="inline-flex h-10 shrink-0 items-center rounded-full bg-white px-4 text-[14px] font-medium ring-1 ring-[#E4E0D7] ring-inset transition-colors hover:bg-[#FAF9F6]"
                  style={{ color: INK }}
                >
                  {current.status === 'open' ? t('care.staff.close') : t('care.staff.reopen')}
                </button>
              </header>
              <ThreadView key={current.id} thread={current} side="staff" />
            </>
          ) : (
            <div className="flex min-h-[320px] flex-col items-center justify-center text-center">
              <EventArt kind="meeting" tone="lavender" size={88} />
              <p
                className="mt-3 font-display text-[22px] leading-7 font-light tracking-[-0.02em]"
                style={{ color: INK_SOFT }}
              >
                {t('care.staff.pick')}
              </p>
            </div>
          )}
        </Block>
      </div>
    </div>
  );
}

const HARD = ['sad', 'angry', 'anxious'] as const;

/** One class: a bar of the share of hard days, the number big and light, the change since last week. */
function MoodRow({ row, previous }: { row: ClassMood; previous?: ClassMood }) {
  const { t } = useTranslation();
  const percent = Math.round(row.hardShare * 100);
  const before = previous ? Math.round(previous.hardShare * 100) : undefined;
  const breakdown = MOODS.map((m) => `${t(`care.moods.${m}`)} ${row.counts[m]}`).join(' · ');
  const [from, to] = DEEP.lavender;
  return (
    <li
      className="grid grid-cols-[52px_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 py-3 sm:grid-cols-[60px_minmax(0,1fr)_150px] sm:gap-x-4"
      style={{ borderTop: `1px solid ${LINE}` }}
      title={breakdown}
    >
      <span
        className="font-display text-[24px] leading-none font-light tracking-[-0.03em]"
        style={{ color: INK }}
      >
        {row.classLabel}
      </span>
      <span className="min-w-0">
        <span
          className="block h-2.5 overflow-hidden rounded-full"
          style={{ backgroundColor: PAPER }}
        >
          <span
            className="block h-full rounded-full transition-[width] duration-700"
            style={{
              width: `${Math.max(percent, 2)}%`,
              backgroundImage: `linear-gradient(90deg, ${from} 0%, ${to} 100%)`,
            }}
          />
        </span>
        <span
          className="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px]"
          style={{ color: INK_SOFT }}
        >
          <span className="mr-0.5">{t('care.staff.respondents', { count: row.respondents })}</span>
          {MOODS.map((m) => (
            <span key={m} className="inline-flex items-center gap-1" title={t(`care.moods.${m}`)}>
              <MoodArt mood={m} size={15} />
              {row.counts[m]}
            </span>
          ))}
        </span>
      </span>
      <span className="text-right">
        <span
          className="block font-display text-[26px] leading-8 font-light tracking-[-0.03em]"
          style={{ color: INK }}
        >
          {percent}%
        </span>
        {before !== undefined && before !== percent && (
          <span
            className="inline-flex items-center gap-1 text-[12px]"
            style={{ color: percent > before ? GRAD.coral.ink : GRAD.mint.ink }}
          >
            {percent > before ? (
              <ArrowUpIcon size={11} weight="bold" aria-hidden />
            ) : (
              <ArrowDownIcon size={11} weight="bold" aria-hidden />
            )}
            <span className="hidden sm:inline">
              {t('care.staff.lastWeekWas', { percent: before })}
            </span>
            <span className="sm:hidden">{before}%</span>
          </span>
        )}
      </span>
    </li>
  );
}

function MoodOverview({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation();
  const today = todayIso();
  const week = useMoodOverview(schoolId, addDays(today, -6), today);
  const last = useMoodOverview(schoolId, addDays(today, -13), addDays(today, -7));
  const rows = [...(week.data ?? [])].sort((a, b) => b.hardShare - a.hardShare);
  return (
    <Block>
      <Head title={t('care.staff.mood')} note={t('care.staff.week')} />
      <Private className="-mt-2 mb-4">{t('care.staff.moodHint')}</Private>
      <p
        className="mb-1 flex items-center gap-1.5 text-[12.5px] font-medium"
        style={{ color: INK_FAINT }}
      >
        <span className="first-letter:uppercase">{t('care.staff.hard')}</span>
        {HARD.map((m) => (
          <MoodArt key={m} mood={m} size={16} />
        ))}
      </p>
      {week.isPending ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState>{t('care.staff.noMood')}</EmptyState>
      ) : (
        <ul className="flex flex-col">
          {rows.map((row) => (
            <MoodRow
              key={row.classId}
              row={row}
              previous={(last.data ?? []).find((p) => p.classId === row.classId)}
            />
          ))}
        </ul>
      )}
    </Block>
  );
}

/**
 * «Турбота» for those who answer: the psychologist (all threads, the mood of classes, meetings)
 * and the director (bullying reports only).
 */
export function StaffCare({
  ctx,
}: {
  ctx: Extract<ViewContext, { kind: 'psychologist' | 'admin' }>;
}) {
  const { t } = useTranslation();
  const psychologist = ctx.kind === 'psychologist';
  const [tab, setTab] = useState<'inbox' | 'mood'>('inbox');
  const switcher = (
    <>
      <Tabs
        label={t('care.title')}
        value={tab}
        onChange={setTab}
        options={[
          { id: 'inbox', label: t('care.staff.inbox') },
          { id: 'mood', label: t('care.staff.mood') },
        ]}
      />
      <Link
        to="/meetings"
        aria-label={t('care.staff.meetings')}
        title={t('care.staff.meetings')}
        className={`${ringButton} sm:hidden`}
      >
        <ArrowUpRightIcon size={16} weight="bold" aria-hidden />
      </Link>
      <span className="hidden sm:block">
        <Link to="/meetings" className={pillClass('light')}>
          {t('care.staff.meetings')}
          <PillIcon icon={ArrowUpRightIcon} />
        </Link>
      </span>
    </>
  );
  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('care.title')}
        sub={
          <Private className="max-w-[720px] !text-[15px] !leading-[22px]">
            {psychologist ? t('care.staff.privacy') : t('care.staff.directorOnly')}
          </Private>
        }
      >
        {psychologist && <span className="hidden items-center gap-2 md:flex">{switcher}</span>}
      </PageHead>
      {/* on a phone the switch under the title (the title keeps its width) */}
      {psychologist && <div className="-mt-1 flex items-center gap-2 md:hidden">{switcher}</div>}
      {tab === 'mood' && psychologist ? (
        <MoodOverview schoolId={ctx.schoolId} />
      ) : (
        <Inbox ctx={ctx} directorOnly={!psychologist} />
      )}
    </div>
  );
}
